import { useState, useEffect } from "react";
import { base44 } from '@/api/base44Client';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ConfiguratorStepSlider from "../components/configurator/ConfiguratorStepSlider";
import { CONFIGURATOR_DATA_GEILO, BASE_PRICE_GEILO } from "../components/configurator/configuratorDataGeilo";
import { useSaunaBasePrice } from "../hooks/useSaunaBasePrice";
import { useSaunaOptionPrices } from "../hooks/useSaunaOptionPrices";

const STORAGE_KEY = 'sauna_geilo_config';

function getDefaultConfig() {
  return CONFIGURATOR_DATA_GEILO.filter(s => !s.isSummary).reduce((acc, section) => {
    const defaultOption = section.options.find(o => o.default);
    const defaultValue = defaultOption?.id || section.options[0]?.id;
    return { ...acc, [section.id]: section.multiselect ? [defaultValue] : defaultValue };
  }, {});
}

export default function SaunaGeiloKonfigurator() {
  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : getDefaultConfig();
  });
  const [submitted, setSubmitted] = useState(false);
  const { basePrice: dynamicBasePrice, loading: priceLoading } = useSaunaBasePrice('geilo', BASE_PRICE_GEILO);
  const { pricesMap: priceMap, loading: optionPricesLoading } = useSaunaOptionPrices('geilo');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  }, [config]);

  const totalPrice = dynamicBasePrice + CONFIGURATOR_DATA_GEILO.filter(s => !s.isSummary).reduce((sum, step) => {
    const sel = config[step.id];
    const ids = Array.isArray(sel) ? sel : [sel];
    const price = ids.reduce((p, id) => {
      const opt = step.options.find(o => o.id === id);
      const optionPrice = priceMap[id] !== undefined ? priceMap[id] : (opt?.price || 0);
      return p + optionPrice;
    }, 0);
    return sum + price;
  }, 0);

  const handleUpdate = (stepId, optionId) => {
    setConfig(prev => {
      const step = CONFIGURATOR_DATA_GEILO.find(s => s.id === stepId);
      let updated;
      if (step?.multiselect) {
        const current = Array.isArray(prev[stepId]) ? prev[stepId] : [];
        updated = { ...prev, [stepId]: current.includes(optionId) ? current.filter(id => id !== optionId) : [...current, optionId] };
      } else {
        updated = { ...prev, [stepId]: optionId };
      }
      return updated;
    });
  };

  const resetConfig = () => {
    const newConfig = getDefaultConfig();
    setConfig(newConfig);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleSubmit = () => {
    base44.analytics.track({
      eventName: "sauna_configuration_completed",
      properties: { model: "geilo", success: true }
    });
    setSubmitted(true);
  };

  if (priceLoading || optionPricesLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground font-heading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-border border-t-foreground rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background text-foreground font-heading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center max-w-lg">
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Konfigurasjon sendt</p>
            <h2 className="font-heading text-4xl md:text-6xl font-light leading-[1.1] mb-6">Takk <br /><span className="italic">for forespørselen.</span></h2>
            <div className="w-12 h-px bg-primary/40 mx-auto mb-8" />
            <p className="font-body text-lg leading-relaxed text-foreground/60 mb-10">Vi kontakter deg innen 24 timer.</p>
            <button onClick={() => { setSubmitted(false); resetConfig(); }} className="text-sm tracking-widest-xl font-body uppercase px-8 py-4 border border-foreground/30 text-foreground hover:bg-foreground hover:text-background transition-all">Ny konfigurasjon</button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-heading">
      <Navbar />
      <div className="pt-32 pb-8 px-6 md:px-12 max-w-[1440px] mx-auto">
        <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">LYKKE GEILO</p>
        <h1 className="font-heading text-3xl md:text-5xl font-light leading-[1.1] mb-2">Konfigurer din badstue</h1>
        <p className="font-heading text-lg text-foreground/60 mb-8">Tilpass din LYKKE GEILO — velg interiør, tilbehør og ekstra.</p>
      </div>
      <div className="px-6 md:px-12 max-w-[1440px] mx-auto pb-20">
        <ConfiguratorStepSlider config={config} onUpdate={handleUpdate} steps={CONFIGURATOR_DATA_GEILO} totalPrice={totalPrice} basePrice={dynamicBasePrice} onSubmit={handleSubmit} priceMap={priceMap} />
      </div>
      <Footer />
    </div>
  );
}